import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { I18nProvider } from "@/i18n/I18nProvider";
import Home from "@/pages/Home";
import Exercices from "@/pages/Exercices";
import Biography from "@/pages/Biography";
import Admin from "@/pages/Admin";
import AdminSetupE2E from "@/pages/AdminSetupE2E";
import LocalPage from "@/pages/LocalPage";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import GiftCards from "@/pages/GiftCards";
import ChairMassage from "@/pages/ChairMassage";
import HomeMassage from "@/pages/HomeMassage";
import Events from "@/pages/Events";
import Menu from "@/pages/Menu";
import ComfortTools from "@/pages/ComfortTools";
import CityPage from "@/pages/CityPage";
import StylePage from "@/pages/StylePage";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ScrollToHash from "@/components/ScrollToHash";

// Alias courts client-friendly : /saint-jerome → /massage-saint-jerome
// Google n'indexe que les URLs longues (canonique préservé via useSEO),
// les alias servent uniquement à ce que taper une URL courte tombe au bon endroit.
const CITY_SHORTCUTS = [
    "saint-jerome", "sainte-adele", "saint-adolphe", "morin-heights",
    "saint-sauveur", "val-morin", "val-david", "sainte-sophie",
    "saint-hippolyte", "prevost", "saint-eustache",
    "montreal", "laval",
    "sherbrooke", "rock-forest", "estrie",
];
const STYLE_SHORTCUTS = [
    "thai", "suedois", "californien", "lomi-lomi", "deep-tissue", "sur-chaise",
];

function App() {
    return (
        <div className="App">
            <I18nProvider>
                <BrowserRouter>
                    <ScrollToHash />
                    <Navbar />
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/exercices" element={<Exercices />} />
                        <Route path="/blog" element={<Blog />} />
                        <Route path="/blog/:slug" element={<BlogPost />} />
                        <Route path="/cartes-cadeaux" element={<GiftCards />} />
                        <Route path="/massage-entreprise" element={<ChairMassage />} />
                        <Route path="/massage-domicile" element={<HomeMassage />} />
                        <Route path="/evenements" element={<Events />} />
                        <Route path="/menu" element={<Menu />} />
                        <Route path="/confort-et-outils" element={<ComfortTools />} />
                        <Route path="/outils" element={<Navigate to="/confort-et-outils" replace />} />
                        <Route path="/biographie" element={<Biography />} />
                        <Route path="/blainville" element={<LocalPage location="blainville" />} />
                        <Route path="/rive-nord" element={<LocalPage location="rive-nord" />} />

                        {/* SEO — massage styles */}
                        <Route path="/massage-thai" element={<StylePage />} />
                        <Route path="/massage-suedois" element={<StylePage />} />
                        <Route path="/massage-californien" element={<StylePage />} />
                        <Route path="/massage-lomi-lomi" element={<StylePage />} />
                        <Route path="/massage-deep-tissue" element={<StylePage />} />
                        <Route path="/massage-sur-chaise" element={<StylePage />} />

                        {/* SEO — cities (Laurentides à domicile) */}
                        <Route path="/massage-saint-jerome" element={<CityPage />} />
                        <Route path="/massage-sainte-adele" element={<CityPage />} />
                        <Route path="/massage-saint-adolphe" element={<CityPage />} />
                        <Route path="/massage-morin-heights" element={<CityPage />} />
                        <Route path="/massage-saint-sauveur" element={<CityPage />} />
                        <Route path="/massage-val-morin" element={<CityPage />} />
                        <Route path="/massage-val-david" element={<CityPage />} />
                        <Route path="/massage-sainte-sophie" element={<CityPage />} />
                        <Route path="/massage-saint-hippolyte" element={<CityPage />} />
                        <Route path="/massage-prevost" element={<CityPage />} />
                        <Route path="/massage-saint-eustache" element={<CityPage />} />

                        {/* SEO — Grand Montréal (clinic) */}
                        <Route path="/massage-montreal" element={<CityPage />} />
                        <Route path="/massage-laval" element={<CityPage />} />

                        {/* SEO — Estrie (future 2027) */}
                        <Route path="/massage-sherbrooke" element={<CityPage />} />
                        <Route path="/massage-rock-forest" element={<CityPage />} />
                        <Route path="/massage-estrie" element={<CityPage />} />

                        {/* Alias SEO massage-* pour services (bon pour Google) */}
                        <Route path="/massage-evenement" element={<Navigate to="/evenements" replace />} />
                        <Route path="/massage-evenements" element={<Navigate to="/evenements" replace />} />
                        <Route path="/massage-clinique" element={<Navigate to="/blainville" replace />} />
                        <Route path="/massage-cabinet" element={<Navigate to="/blainville" replace />} />
                        {/* Nouveaux services SEO (redirection vers /menu où tout est détaillé) */}
                        <Route path="/massage-sur-vetement-habille" element={<Navigate to="/menu#vetement" replace />} />
                        <Route path="/massage-habille" element={<Navigate to="/menu#vetement" replace />} />
                        <Route path="/massage-psychocorporel" element={<Navigate to="/menu#psychocorporel" replace />} />
                        <Route path="/massage-cible" element={<Navigate to="/menu#cible" replace />} />
                        <Route path="/massage-therapeutique-cible" element={<Navigate to="/menu#cible" replace />} />
                        <Route path="/massage-chaise-individuel" element={<Navigate to="/massage-entreprise" replace />} />

                        {/* Alias courts client-friendly */}
                        <Route path="/entreprise" element={<Navigate to="/massage-entreprise" replace />} />
                        <Route path="/evenement" element={<Navigate to="/evenements" replace />} />
                        <Route path="/domicile" element={<Navigate to="/massage-domicile" replace />} />
                        <Route path="/clinique" element={<Navigate to="/blainville" replace />} />
                        <Route path="/cabinet" element={<Navigate to="/blainville" replace />} />
                        <Route path="/habille" element={<Navigate to="/menu#vetement" replace />} />
                        <Route path="/psychocorporel" element={<Navigate to="/menu#psychocorporel" replace />} />
                        <Route path="/cible" element={<Navigate to="/menu#cible" replace />} />
                        <Route path="/chaise" element={<Navigate to="/massage-entreprise" replace />} />
                        {CITY_SHORTCUTS.map((slug) => (
                            <Route
                                key={`shortcut-${slug}`}
                                path={`/${slug}`}
                                element={<Navigate to={`/massage-${slug}`} replace />}
                            />
                        ))}
                        {STYLE_SHORTCUTS.map((slug) => (
                            <Route
                                key={`shortcut-style-${slug}`}
                                path={`/${slug}`}
                                element={<Navigate to={`/massage-${slug}`} replace />}
                            />
                        ))}

                        <Route path="/admin" element={<Admin />} />
                        <Route path="/admin/setup-e2e" element={<AdminSetupE2E />} />
                    </Routes>
                    <Footer />
                    <Toaster position="top-right" richColors />
                </BrowserRouter>
            </I18nProvider>
        </div>
    );
}

export default App;
